/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import { isEmpty, some, uniq } from 'lodash';

import React, { Fragment } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Route, Switch, Link } from 'react-router-dom';
import otapi from '@opentelemetry/api';

import MainLayout from 'components/layouts/MainLayout';
import PreferencesLayout from 'components/layouts/PreferencesLayout';
import { logoutUrl } from 'services/Externals';
import BenefitsLayout from 'components/layouts/BenefitsLayout';
import ScrollToTop from 'components/layouts/ScrollToTop';
import ExternalAuthorizationForm from 'components/shared/ExternalAuthorizationForm/ExternalAuthorizationForm';
import ErrorPage from 'components/shared/ErrorPage/ErrorPage';
import {
  getClientMissions,
  getPrimaryData,
  getRequiredMinimumDistribution,
  registerLogoutCallback
} from 'services/Rest';
import {
  aamLogoutUrl,
  aamLoginUrl,
  clientPortalDashboardUrl,
  guestUserHomepageUrl,
  workplaceRetirementAccountsUrl,
  clientPortalDashboardInternalUrl
} from "services/Externals";
import Store from 'state/Store';
import { getActiveOwnedAccountsWithWorkplace } from 'state/account/AccountSelectors';
import {
  CWV_ENTITLEMENT,
  RETAIL_ENTITLEMENT,
  RPS_ENTITLEMENT,
  SALESFORCE_ENTITLEMENT,
  PAM_ENTITLEMENT
} from 'state/view/ViewReducer';
import {
  storeQueryParams,
  setAppEntryPoint,
  setSalesforceRepEnabled
} from 'state/session/SessionActions';
import {
  layoutChanged,
  MOBILE_LAYOUT,
  DESKTOP_LAYOUT,
  SALESFORCE_CHECK
} from 'state/view/ViewActions';
import { benefitsRoutes } from 'components/layouts/Navigation/NavigationConfig';
import NoAccountsPage from 'components/shared/ErrorPage/NoAccountsPage';
import ExcessAccountsPage from 'components/shared/ErrorPage/ExcessAccountsPage';
import HasUnfinishedApplication from 'components/shared/ErrorPage/HasAccountMessagePage';
import BrokerageErrorPage from 'components/shared/ErrorPage/BrokerageErrorPage';
import WsjErrorPage from 'components/shared/ErrorPage/WsjErrorPage';
import RetailTracSSOErrorPage from './components/shared/ErrorPage/RetailTracSSOErrorPage';
import MissingInfoErrorPage from 'components/shared/ErrorPage/MissingInfoErrorPage';
import NoAddressErrorPage from 'components/shared/ErrorPage/NoAddressErrorPage';
import SummitZeroStatePage from 'components/shared/ErrorPage/SummitZeroStatePage';
import MessageCenterLayout from 'components/layouts/MessageCenterLayout';
import PlanningDetailsRedirect from 'components/layouts/PlanningDetailsRedirect';
import {
  getAppEntryPoint,
  isContextualNavVisible,
  isMainMenuVisible
} from 'state/session/SessionSelectors';
import {
  getCustomerWebView,
  getCustomerForNavigation
} from 'state/customer/CustomerSelectors';
import { getAssetId } from 'state/household/HouseholdInformationSelectors';
import CustomVideoPage from 'components/pages/CustomVideo/CustomVideoPage';
import ExternalInvestmentConsentModal from 'components/shared/Modal/ExternalInvestmentConsentModal/ExternalInvestmentConsentModal';
import {isSalesforceRepEnabled, isMobile, canViewCheckwriting} from 'state/view/ViewSelectors';
import {
  accountRegistrationEnabled,
  brokerageFrameEnabled,
  clientMissionsEnabled,
  clientPortalEnabled,
  isBrokerageTradingEnabled,
  redirectToClientPortalEnabled,
  retailTracEnabled
} from 'services/FeatureFlags';
import AccountCenterPage from 'components/pages/AccountCenter/AccountCenterPage';
import LogoutPage from 'components/pages/Logout/LogoutPage';
import { withAuthState } from '@trp-ta-nitro/auth-react';
import { receivedUserEntitlements } from 'state/session/SessionActions';
import {
  getCookieValue,
  getQueryStringParams,
  hasBrowserCookie,
  isBrokerageIframe,
  setCookie
} from 'support/BrowserUtils';
import { getProducts } from 'services/Products';
import MorningstarRegistrationCallback from 'components/layouts/HouseholdInformationCard/MorningstarRegistrationCallback';
import {
  getBaseUrl,
  getENV,
  GLOBALS,
  isInternalContext,
  isSandbox,
  TRP_ENVIRONMENTS,
} from 'services/globals';
import { SecureNavigationBeacon, SessionTimeout } from '@trp-ii-ii-component-library/ii-components';
import AccountRegistrationPage from 'components/layouts/Preferences/AccountRegistration/AccountRegistrationPage';
import { redirectToNewDefaultHomepage } from 'state/preferences/PreferencesSelectors';
import {
  updateAdobeAnalytics,
  updateQualtricsAnalytics
} from 'support/Analytics';
import {
  hasAppLoaded,
  hasAdobeData,
  hasQualtricsData,
  hasAppError,
  hasCurrentRouteLoaded
} from 'state/analytics/analyticsSelectors';
import {
  getHasReceivedSecureNavigationData,
  getRequestStatus
} from 'state/requests/RequestStatusSelectors';
import { withOtelContext, withSpanRegistrar } from '@trp-ta-nitro/otel-react';
import { finalizeApploadSpan } from './services/Observability/SpanReference';
import { getObservabilityMetadata } from './services/Observability/ObservabilitySelectors';
import { getTracer } from './services/Observability/otel';
import BrokerageExperience from './components/pages/BrokerageExperience/BrokerageExperience';
import RpsOnlyPage from 'components/shared/ErrorPage/RpsOnlyPage';
import RetailTracAndWorkplaceOnlyPage from 'components/shared/ErrorPage/RetailTracAndWorkplaceOnlyPage';
import ExternalAuthorizationTracForm from 'components/shared/ExternalAuthorizationTracForm/ExternalAuthorizationTracForm';
import FixedIncomeResearch from 'components/layouts/Research/FixedIncomeResearch';
import QuotesResearch from 'components/layouts/Research/QuotesResearch';
import { isDashboardOnly, isWorkplaceNavigation } from "state/router/RouterSelectors";
import ProjectedCashFlow from "components/layouts/ProjectedCashFlow/ProjectedCashFlow";
import BrokerageTradingMFE from "components/layouts/Trading/Trading";
export const SendTo = ({ target }) => {
  if (window && window.location) {
    window.location.replace(target);
  }
  return null;
};

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      adobeUpdated: false,
      qualtricsUpdated: false,
      reportSent: false
    };
    this.changeLayout = this.changeLayout.bind(this);
    this.mq = window.matchMedia('screen and (max-width: 767px)');
    this.mq.addListener(this.changeLayout);
  }

  componentDidMount() {
    otapi.context.with(this.props.otelContext, () => {
      const contentStartSpan = getTracer().startSpan('content-started');
      contentStartSpan.end();
    });
    this.changeLayout();
    const {
      apiKey,
      authState,
      isCustomerWebView,
      isDashboardOnly,
      router: {
        location: { pathname }
      }
    } = this.props;
    const { userId, entitlements: receivedEntitlements } = authState || {};
    const user = userId ? userId : '';
    const entitlements =
      receivedEntitlements && receivedEntitlements.length > 0
        ? receivedEntitlements
        : [];
    Store.dispatch(receivedUserEntitlements(user, entitlements));
    if (clientMissionsEnabled()) {
      try {
        getClientMissions();
      } catch (e) {
        //do nothing
      }
    }
    try {
      getRequiredMinimumDistribution();
    } catch (e) {
      //do nothing
    }

    const redirectToClientPortal = () => {
      if( getCookieValue(document.cookie,'client-portal') === 'true' ) {
        return true;
      } else if(  redirectToClientPortalEnabled() && !hasBrowserCookie('aam-dashboard') && !isSandbox() ) {
        setCookie('aam-dashboard', 'false');
        setCookie('client-portal', 'true');
        return true;
      }
      return false;
    };

    if(clientPortalEnabled() && isDashboardOnly &&
      redirectToClientPortal()
    ){
      if(isInternalContext(getBaseUrl())) {
        SendTo({
          target: clientPortalDashboardInternalUrl
        });
      } else {
        SendTo({
          target: clientPortalDashboardUrl
        });
      }
    }
    if (
      !isCustomerWebView &&
      !entitlements.some(
        (entitlement) =>
          entitlement === RETAIL_ENTITLEMENT ||
          entitlement === PAM_ENTITLEMENT ||
          entitlement === CWV_ENTITLEMENT ||
          entitlement === SALESFORCE_ENTITLEMENT
      )
    ) {
      if (entitlements.some((entitlement) => entitlement === RPS_ENTITLEMENT)) {
        // rps redirect
        SendTo({
          target: workplaceRetirementAccountsUrl
        });
      } else {
        // you aren't Retail, RPS or PAM what are you doing? Are you a guest ?
        SendTo({
          target: guestUserHomepageUrl
        });
      }
    }
    try {
      const logoutCallback = [
        {
          app: 'accountaccess',
          type: 'fetch',
          url: `${GLOBALS.AAM_BFF_BASE_URL}/api/logout`,
          apiKey
        }
      ];

      // registerLogoutService(apiKey);
      registerLogoutCallback(logoutCallback);
    } catch (e) {
      // don't let the session kill anything - swallow error
    }

    if (pathname) {
      Store.dispatch(setAppEntryPoint(pathname));
    }

    const queryStringPairs = getQueryStringParams();

    // TODO: pass location search from props to store this
    Store.dispatch(storeQueryParams(queryStringPairs));

    // Check if this is a Salesforce Rep
    const isInternalDomain = isInternalContext(getBaseUrl());
    const isSalesforceRep = queryStringPairs.filter(
      (q) => q['isSalesforceRep']
    ).length;
    const isSalesforce = isSalesforceRep && (isSandbox || isInternalDomain);

    if (isSalesforce) {
      Store.dispatch(setSalesforceRepEnabled(SALESFORCE_CHECK));
    }

    const isInternal =
      some(
        queryStringPairs,
        (param) => param.delegateUser && !isEmpty(param.delegateUser)
      ) || isCustomerWebView;

    otapi.context.with(this.props.otelContext, () => {
      this.initializeData(isInternal);
    });
  }

  componentDidUpdate(prevProps) {
    const { currentRouteLoaded, hasAdobeData, hasQualtricsData } = this.props;
    const { adobeUpdated, qualtricsUpdated, reportSent } = this.state;

    if (hasAdobeData && !adobeUpdated) {
      updateAdobeAnalytics();
      this.setState({ adobeUpdated: true });
    }
    if (hasQualtricsData && !qualtricsUpdated) {
      updateQualtricsAnalytics();
      this.setState({ qualtricsUpdated: true });
    }

    if (!prevProps.currentRouteLoaded && currentRouteLoaded) {
      otapi.context.with(this.props.otelContext, () => {
        const depsCompleteSpan = getTracer().startSpan('dependencies-loaded');
        depsCompleteSpan.end();
      });
      const { observabilityMetadata } = this.props;
      otapi.context.with(this.props.otelContext, () => {
        const pageCompleteSpan = getTracer().startSpan('page-load-complete');
        pageCompleteSpan.end();
      });
      if (
        !reportSent ||
        (window &&
          window.location &&
          window.location.pathname == '/usis/my-accounts/post-logout')
      ) {
        finalizeApploadSpan(observabilityMetadata);
        this.setState({ reportSent: true });
      }
    }
  }

  initializeData = (isInternal) => {
    getPrimaryData(isInternal)
      .then(() => {
        otapi.context.with(this.props.otelContext, () => {
          const pageVisibleSpan = getTracer().startSpan('page-visible');
          pageVisibleSpan.end();
        });
      })
      .catch((err) => console.error(err));
  };

  changeLayout() {
    const newLayout = this.mq.matches ? MOBILE_LAYOUT : DESKTOP_LAYOUT;
    Store.dispatch(layoutChanged(newLayout));
  }
  render() {
    const {
      isSalesforceRepEnabled,
      accounts,
      authState,
      customer,
      router,
      showMainMenu,
      showContextualNav,
      isMobile,
      version,
      hasReceivedSecureNavigationData,
      viewCheckwriting,
      isWorkplaceNavigationVisible,
      isCustomerWebView
    } = this.props;
    const { userId } = authState || {};
    const user = userId ? userId : '';
    const products = uniq(getProducts(accounts));
    const isSecureNavigationLoading = !hasReceivedSecureNavigationData;
    const showBeaconNavMainMenu = showMainMenu && !isWorkplaceNavigationVisible;
    const brokerageMFEEnv = getENV() === TRP_ENVIRONMENTS.PRODUCTION ? 'netx' : 'xat';
    return (
      <Fragment>
        <SessionTimeout
          apiKey={this.props.apiKey}
          keepAliveUrl={isCustomerWebView ? `${GLOBALS.AAM_INTERNAL_BASE_URL}${GLOBALS.AAM_UI_ACCOUNT_URL}/api/ping` : `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_ACCOUNT_URL}/api/ping`}
          logoutURL={logoutUrl(products)}
        />
        {/** After a common layout has been created that can house all content in preferences and main then this could move there.  **/}
        <ExternalInvestmentConsentModal
          disableClickOff
          containerClassName="external-account-consent-modal"
          hideClose={true}
          title={''}
          hideAnchor={true}
          label="external-account-consent-modal"
        />
        {/** Pershing MFE Shell **/}
        <per-mfe-shell initialsettings={`{
          "environment": "${brokerageMFEEnv}",
          "ibdName": "T. Rowe Price",
          "contactInfo": {
            "techSupportPhone": "1-800-225-7720",
            "custSupportPhone": "1-800-225-7720"
          },
          "ibd": "30A",
          "platform": "retail"
        }`}></per-mfe-shell>
        <ScrollToTop>
          <div className="hide-nav-print">
            {!isMobile && !isSalesforceRepEnabled && !isBrokerageIframe() && (
              <SecureNavigationBeacon
                authentication={{
                  authorization: {
                    isCustomerWebView: customer.webViewEnabled,
                    isAuthenticated: !isEmpty(user)
                  },
                  loginUrl: aamLoginUrl(products),
                  logoutUrl: aamLogoutUrl(),
                  user: customer
                }}
                data={{
                  brokerageTradingEnabled: brokerageFrameEnabled(),
                  isFixedIncomeEnabled: true,
                  accounts,
                  canViewCheckwriting: viewCheckwriting,
                  showMainMenu: showBeaconNavMainMenu,
                  showContextualNav,
                  version,
                  isLoading: isSecureNavigationLoading,
                  showLoadingBar: false
                }}
                providedRouter={{
                  router,
                  Link
                }}
              />
            )}
          </div>
          <Switch>
            <Route exact component={LogoutPage} path="/logout" />
            <Route
              exact
              path="/external-authorization"
              component={ExternalAuthorizationForm}
            />
            {retailTracEnabled() && !isCustomerWebView && (
              <Route
                exact
                path="/smallbusiness-external-authorization"
                component={ExternalAuthorizationTracForm}
              />
            )}
            <Route component={ErrorPage} path="*/error" />
            <Route component={HasUnfinishedApplication} path="/saved-nasu" />
            <Route component={NoAccountsPage} path="/no-accounts" />
            <Route component={SummitZeroStatePage} path="/program-benefits-ineligible" />
            <Route component={ExcessAccountsPage} path="/excess-accounts" />
            <Route component={RpsOnlyPage} path="/workplace" />

            {retailTracEnabled() && (
              <Route
                component={RetailTracAndWorkplaceOnlyPage}
                path="/workplace-small-business"
              />
            )}

            <Route
              component={BrokerageErrorPage}
              path="/brokerage-unavailable"
            />
            <Route component={WsjErrorPage} path="/wsj-unavailable" />
            {retailTracEnabled() && (
              <Route
                component={RetailTracSSOErrorPage}
                path="/smallbusiness-unavailable"
              />
            )}
            <Route
              component={MissingInfoErrorPage}
              path="/missing-information"
            />
            <Route component={NoAddressErrorPage} path="/missing-address" />
            {benefitsRoutes.map((route, i) => (
              <Route component={BenefitsLayout} key={i} path={route} />
            ))}

            {accountRegistrationEnabled() && (
              <Route
                component={AccountRegistrationPage}
                path="/profile/account-registration"
              />
            )}
            <Route component={PreferencesLayout} path="/profile" />
            <Route component={CustomVideoPage} path="/custom-video" />
            <Route
              component={AccountCenterPage}
              path="/external-account-center"
            />
            <Route component={MessageCenterLayout} path="/message-center" />
            <Route
              component={PlanningDetailsRedirect}
              path="/ras-home"
            />
            {brokerageFrameEnabled() && (
              <Route component={BrokerageExperience} path="/brokerage" />
            )}
            <Route
              component={MorningstarRegistrationCallback}
              path="/morningstar/registration"
            />
            <Route
              component={FixedIncomeResearch}
              path="/research/fixed-income"
            />
            <Route
              component={QuotesResearch}
              path="/research/market-summary"
            />
            <Route
              component={ProjectedCashFlow}
              path="/projected-cash-flow"
            />
            {isBrokerageTradingEnabled() && (
              <Route
                component={BrokerageTradingMFE}
                path="/trading"
              />
            )}
            <Route component={MainLayout} />
          </Switch>
        </ScrollToTop>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  appLoaded: hasAppLoaded(state),
  observabilityMetadata: getObservabilityMetadata(state),
  currentRouteLoaded: hasCurrentRouteLoaded(state),
  entryPoint: getAppEntryPoint(state),
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  customer: getCustomerForNavigation(state),
  clientTier: getAssetId(state),
  hasAppError: hasAppError(state),
  session: state.session,
  accounts: getActiveOwnedAccountsWithWorkplace(state),
  showMainMenu: isMainMenuVisible(state),
  showContextualNav: isContextualNavVisible(state),
  isDashboardOnly: isDashboardOnly(state),
  isCustomerWebView: getCustomerWebView(state),
  isMobile: isMobile(state),
  redirectedToAccountsPage: redirectToNewDefaultHomepage(state),
  router: state.router,
  view: state.view,
  hasAdobeData: hasAdobeData(state),
  hasQualtricsData: hasQualtricsData(state),
  requestStatus: getRequestStatus(state),
  hasReceivedSecureNavigationData: getHasReceivedSecureNavigationData(state),
  viewCheckwriting: canViewCheckwriting(state),
  isWorkplaceNavigationVisible: isWorkplaceNavigation(state)
});

export default compose(
  connect(mapStateToProps),
  withAuthState,
  withOtelContext,
  withSpanRegistrar
)(App);
